// File: src/pages/ReceiptList.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
} from "reactstrap";

import swal from "sweetalert";

import { FaPlus, FaFilter, FaPencilAlt } from "react-icons/fa";
import { ImDownload } from "react-icons/im";
import { Trash2 } from "react-feather";

import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";

import {
  _Get,
  _Delete,
  _Put,
  CreateCustomerList,
} from "../import/ApiEndPoint/ApiCalling";
import {
  View_Receipt,
  Delete_Receipt_By_Id,
  View_Company_Details,
  Create_Account_List,
  View_Expense_Account,
  Update_Receipt_By_Id2,
} from "../import/ApiEndPoint/Api";

const View_Pending_Receipt = String(View_Receipt || "").replace(
  "view-reciept",
  "view-pending-reciept",
);

// ---------- tiny, page-scoped CSS injected once ----------
const injectOnce = () => {
  if (document.getElementById("receiptlist-inline-css")) return;
  const style = document.createElement("style");
  style.id = "receiptlist-inline-css";
  style.innerHTML = `
    .rc-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .rc-table-wrap{
      width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff;
    }
    .rc-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:800px; }
    .rc-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .rc-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .rc-table tbody tr:hover{ background:#f9fbff; }
    .rc-table tfoot td{
      padding:8px 8px; border-top:2px solid #cbd5e1; background:#f8fafc; font-weight:800; text-align:center;
    }

    .rc-actions{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer; }
    .chip-edit{ background:#d46f10; }
    .chip-delete{ background:#ec1809; }
    .rc-editing-head{ background:#fff7ed !important; color:#9a3412 !important; }
    .rc-editing-cell{ background:#fffbeb; }
    .rc-editing-cell input, .rc-editing-cell select{ min-width:150px; height:32px; font-size:13px; }
    .rc-tabs{ display:flex; gap:6px; padding:3px; border:1px solid #dbe3e8; border-radius:10px; background:#fff; }
    .rc-tab{ border:0; background:transparent; color:#475569; font-weight:800; font-size:12px; padding:7px 11px; border-radius:8px; cursor:pointer; white-space:nowrap; }
    .rc-tab.active{ background:#085bf5; color:#fff; }
    .rc-waiting{ display:inline-flex; align-items:center; justify-content:center; border-radius:999px; background:#fff7ed; color:#9a3412; border:1px solid #fed7aa; font-weight:800; font-size:11px; padding:5px 9px; white-space:nowrap; }
  `;
  document.head.appendChild(style);
};

// ---------- helpers ----------
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const onlyDate = (iso) => (iso ? String(iso).split("T")[0] : "");
const norm = (s) => toStr(s).trim().toLowerCase();
const CASH_ID = "CASH";
const LIST_PAGE_SIZE = 100;
const CASH_PARTY_SID = "CASH7845";

const getEntityId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return String(value?._id || "");
  return String(value);
};

const getFinancialYearLabel = (dateValue = new Date()) => {
  const d =
    typeof dateValue === "string"
      ? new Date(`${dateValue}T00:00:00`)
      : new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const startYear = month >= 4 ? year : year - 1;
  return `${startYear}-${String(startYear + 1).slice(-2)}`;
};

const uniqueById = (items = []) => {
  const seen = new Set();
  return items.filter((item) => {
    const id = String(item?._id || "");
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const buildParticularOptions = (partyList = [], banks = []) => {
  const bankItems = uniqueById(banks).map((b) => ({
    ...b,
    bankAccount: true,
    isCash: String(b?._id || "") === CASH_ID || b?.isCash === true,
  }));

  return [...partyList, ...bankItems].map((it) => {
    if (it?.bankAccount) {
      const isCash = String(it?._id || "") === CASH_ID || it?.isCash === true;
      const name = it?.bankName || "Bank";
      const acc = it?.accountNumber ? ` (${it.accountNumber})` : "";
      return {
        value: `${isCash ? "cash" : "bank"}:${String(it?._id || "")}`,
        label: isCash ? "Cash" : `Bank: ${name}${acc}`,
        kind: isCash ? "cash" : "bank",
        id: String(it?._id || ""),
        raw: it,
      };
    }

    let kind = "expense";
    if (it?.party) kind = "party";
    else if (it?.User) kind = "user";

    return {
      value: `${kind}:${String(it?._id || "")}`,
      label: it?.fullName || "-",
      kind,
      id: String(it?._id || ""),
      raw: it,
    };
  });
};

const runInBatches = async (jobs, batchSize = 15) => {
  for (let i = 0; i < jobs.length; i += batchSize) {
    await Promise.all(jobs.slice(i, i + batchSize).map((job) => job()));
  }
};

// ✅ particular text helper
const getParticularText = (r) => {
  const userFirstName = toStr(r?.userId?.firstName).trim();
  if (userFirstName) return userFirstName;

  return (
    r?.expenseId?.title ||
    r?.expenseId?.bankName ||
    r?.partyId?.CompanyName ||
    r?.transporterId?.companyName ||
    r?.bankDetails?.bankName ||
    r?.bankName ||
    (typeof r?.partyId === "string" ? r.partyId : "") ||
    "NA"
  );
};

// ✅ bank resolvers
const getReceiptBankId = (r) =>
  toStr(
    r?.bankDetails?._id || r?.bankDetails || r?.bankId || r?.bankDetailsId,
  ).trim();

const getReceiptBankName = (r) =>
  toStr(
    r?.bankDetails?.bankName ||
      r?.bankName ||
      r?.expenseId?.bankName ||
      r?.expenseId?.title,
  ).trim();

const getReceiptBankAccount = (r) =>
  toStr(
    r?.bankDetails?.accountNumber ||
      r?.accountNumber ||
      r?.bankAccountNumber ||
      r?.bankAccNo,
  ).trim();

const maskAcc = (acc) => {
  const s = toStr(acc).replace(/\s+/g, "");
  if (!s) return "";
  if (s.length <= 4) return s;
  return `XXXXXX${s.slice(-4)}`;
};

const bankLabel = (b) => {
  const bn = toStr(b?.bankName).trim() || "BANK";
  const br = toStr(b?.branchName).trim();
  const ac = toStr(b?.accountNumber).trim();
  const tail = maskAcc(ac);
  return `${bn}${tail ? ` (${tail})` : ""}${br ? ` - ${br}` : ""}`;
};

const getActiveUserAndDb = () => {
  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const isMaster = user?.rolename?.roleName === "MASTER";

  if (isMaster) {
    try {
      const raw = JSON.parse(
        localStorage.getItem("SuperadminIdByMaster") || "null",
      );
      if (raw) {
        const parts = String(raw).split(" ");
        if (parts?.[0] && parts?.[1]) {
          return { id: parts[0], db: parts[1], isMaster: true };
        }
      }
    } catch {}
  }

  return { id: user?._id, db: user?.database, isMaster };
};

const getSelectedDatabase = (baseDb) => {
  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const financialYearSelected = localStorage.getItem("financialYearSelected");

  const database = baseDb || user?.database || "";

  if (!database || !financialYearSelected) return database;

  return `${database}-${financialYearSelected}`;
};

export default function ReceiptList() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => injectOnce(), []);

  // ------------- state -------------
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [colsModal, setColsModal] = useState(false);

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  const [allRows, setAllRows] = useState([]);
  const [pendingRows, setPendingRows] = useState([]);
  const [activeTab, setActiveTab] = useState("receipts");
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState("");

  // Column-wise edit: one field at a time, no row/column selection checkboxes.
  const [editColumn, setEditColumn] = useState("");
  const [columnDrafts, setColumnDrafts] = useState({});
  const [savingColumn, setSavingColumn] = useState(false);
  const [particularOptions, setParticularOptions] = useState([]);
  const [particularLoading, setParticularLoading] = useState(false);
  const [particularLoadedFor, setParticularLoadedFor] = useState("");
  const [activeContext, setActiveContext] = useState({ uid: "", db: "" });

  // ✅ bank dropdown filter
  const [bankOptions, setBankOptions] = useState([]);
  const [bankFilter, setBankFilter] = useState("");

  // SuperAdmin switch
  const [isMaster, setIsMaster] = useState(false);

  // column chooser state
  const baseColumns = useMemo(
    () => [
      {
        key: "date",
        header: "DATE",
        get: (r) => onlyDate(r?.date),
      },
      {
        key: "particular",
        header: "PARTICULAR",
        get: (r) => getParticularText(r),
      },
      {
        key: "paymentMode",
        header: "PAYMENT MODE",
        get: (r) => r?.paymentMode || "NA",
      },
      { key: "amount", header: "AMOUNT", get: (r) => r?.amount },
      { key: "remark", header: "REMARK", get: (r) => r?.remark || "" },
    ],
    [],
  );

  const handleDeleteRef = useRef(() => {});

  const actionsColumn = useMemo(
    () => ({
      key: "__actions__",
      header: "ACTIONS",
      get: () => "",
      render: (r) =>
        r?.appReceipt === true &&
        String(r?.status || "").toLowerCase() === "deactive" ? (
          <span className="rc-waiting" title="Customer verified; cash collector OTP pending">
            Waiting
          </span>
        ) : (
        <div className="rc-actions">
          {permissions?.Edit && (
            <span
              className="chip chip-edit"
              title="Edit"
              onClick={() =>
                navigate(`/app/receipts/create/${r?._id}`, {
                  state: r,
                })
              }
            >
              <FaPencilAlt size={14} color="white" />
            </span>
          )}
          {permissions?.Delete && (
            <span
              className="chip chip-delete"
              title="Delete"
              onClick={() => handleDeleteRef.current(r?._id)}
            >
              <Trash2 size={14} color="white" />
            </span>
          )}
        </div>
        ),
    }),
    [permissions, navigate],
  );

  const colMap = useMemo(() => {
    const map = new Map();
    baseColumns.forEach((c) => map.set(c.key, c));
    map.set(actionsColumn.key, actionsColumn);
    return map;
  }, [baseColumns, actionsColumn]);

  const addBufferRef = useRef([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // ✅ keep bankFilter valid after company reload
  useEffect(() => {
    if (!bankOptions?.length) {
      setBankFilter("");
      return;
    }

    const ok = bankOptions.some((b) => toStr(b?._id) === bankFilter);

    if (!bankFilter || !ok) {
      setBankFilter(toStr(bankOptions[0]?._id));
    }
  }, [bankOptions, bankFilter]);

  // restore or default columns
  useEffect(() => {
    const raw = localStorage.getItem("ReceiptListCols");
    let nextKeys = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        nextKeys = parsed.filter((k) => colMap.has(k));
      }
    } catch {}
    if (!nextKeys.length) nextKeys = baseColumns.map((c) => c.key);
    setSelectedKeys(nextKeys);
  }, [baseColumns, colMap]);

  // ------------- load company banks -------------
  const loadCompanyBanks = useCallback(async (uid, db) => {
    try {
      const url = `${View_Company_Details}${encodeURIComponent(uid)}/`;
      const res = await _Get(url, db);

      const banks = Array.isArray(res?.CompanyDetail?.bankDetails)
        ? res.CompanyDetail.bankDetails
        : [];

      const cash = {
        _id: CASH_ID,
        bankName: "Cash",
        accountNumber: "",
        isCash: true,
      };

      setBankOptions([...banks, cash]);
    } catch (e) {
      setBankOptions([
        {
          _id: CASH_ID,
          bankName: "Cash",
          accountNumber: "",
          isCash: true,
        },
      ]);
      setBankFilter(CASH_ID);
    }
  }, []);

  // ------------- load receipts -------------
  const reload = useCallback(
    async (uid, db) => {
      if (!uid || !db) {
        setAllRows([]);
        setPendingRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorMsg("");
      setActiveContext({ uid, db });
      setParticularLoadedFor("");
      setParticularOptions([]);

      try {
        const inside = CheckPermission("Receipt");
        if (inside && typeof inside === "object") {
          setPermissions((prev) => ({ ...prev, ...inside }));
        }

        const selectedDatabase = getSelectedDatabase(db);

        const loadActive = async () => {
          try {
            return await _Get(View_Receipt, selectedDatabase);
          } catch (activeError) {
            if (activeError?.response?.status === 404) {
              return { Receipts: [], status: true };
            }
            throw activeError;
          }
        };

        const loadPending = async () => {
          try {
            return await _Get(View_Pending_Receipt, selectedDatabase);
          } catch (pendingError) {
            if (pendingError?.response?.status === 404) {
              return { Receipts: [], status: true };
            }
            throw pendingError;
          }
        };

        // Active receipts and app-created pending verification receipts load together.
        const [, res, pendingRes] = await Promise.all([
          loadCompanyBanks(uid, db),
          loadActive(),
          loadPending(),
        ]);

        const normalizeReceiptRows = (rows) =>
          (Array.isArray(rows) ? rows : [])
            .slice()
            .filter((x) => {
              const t = String(x?.type || "")
                .trim()
                .toLowerCase();
              return t === "receipt" || t.includes("receipt");
            })
            .sort(
              (a, b) =>
                new Date(b?.createdAt || b?.date || 0) -
                new Date(a?.createdAt || a?.date || 0),
            );

        setAllRows(normalizeReceiptRows(res?.Receipts));
        setPendingRows(normalizeReceiptRows(pendingRes?.Receipts));
      } catch (e) {
        console.error("[ReceiptList] fetch error:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load receipts. Check console for details.",
        );
        setAllRows([]);
        setPendingRows([]);
      } finally {
        setLoading(false);
      }
    },
    [loadCompanyBanks],
  );

  const refreshFromActiveContext = useCallback(() => {
    const active = getActiveUserAndDb();
    setIsMaster(Boolean(active?.isMaster));
    reload(active?.id, active?.db);
  }, [reload]);

  // One route-driven load is enough. The previous version also reloaded on
  // mount, focus, pageshow and visibilitychange, which could fire the same
  // expensive APIs several times while opening or returning to this page.
  useEffect(() => {
    refreshFromActiveContext();
  }, [location.key, refreshFromActiveContext]);

  // ------------- visible cols & filters & search -------------
  const visibleCols = useMemo(() => {
    const cols = selectedKeys.map((k) => colMap.get(k)).filter(Boolean);
    return [...cols, actionsColumn];
  }, [selectedKeys, colMap, actionsColumn]);

  const tableCols = visibleCols;

  const filteredRows = useMemo(() => {
    let base =
      activeTab === "pending"
        ? Array.isArray(pendingRows)
          ? pendingRows
          : []
        : Array.isArray(allRows)
          ? allRows
          : [];

    // ✅ bank filter
    // "All Bank Accounts" option removed.
    // If bankFilter is selected, show only that bank's receipts.
    if (activeTab !== "pending" && bankFilter) {
      const sel = (bankOptions || []).find((b) => toStr(b?._id) === bankFilter);
      const isCashFilter = norm(bankFilter) === norm(CASH_ID);

      base = base.filter((r) => {
        const rid = getReceiptBankId(r);
        if (rid && norm(rid) === norm(bankFilter)) return true;

        const rName = getReceiptBankName(r);
        if (sel?.bankName && norm(rName) === norm(sel.bankName)) return true;

        if (isCashFilter) {
          const paymentMode = norm(r?.paymentMode);
          if (paymentMode === "cash") return true;
          if (norm(rName) === "cash") return true;
        }

        const rAcc = getReceiptBankAccount(r);
        const selAcc = toStr(sel?.accountNumber).trim();
        if (selAcc && rAcc && toStr(rAcc) === selAcc) return true;

        return false;
      });
    }

    const term = search.trim().toLowerCase();
    if (!term) return base;

    const searchCols = visibleCols.filter((c) => c.key !== "__actions__");
    return base.filter((r) =>
      searchCols.some((c) => toStr(c.get(r)).toLowerCase().includes(term)),
    );
  }, [
    activeTab,
    allRows,
    pendingRows,
    bankFilter,
    bankOptions,
    search,
    visibleCols,
  ]);

  const totalAmount = useMemo(() => {
    return (filteredRows || []).reduce(
      (sum, r) => sum + (Number(r?.amount) || 0),
      0,
    );
  }, [filteredRows]);

  const effectivePageSize = editColumn === "particular" ? 25 : LIST_PAGE_SIZE;

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRows.length / effectivePageSize),
  );

  const pageRows = useMemo(() => {
    const start = (page - 1) * effectivePageSize;
    return filteredRows.slice(start, start + effectivePageSize);
  }, [filteredRows, page, effectivePageSize]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, bankFilter, search]);

  useEffect(() => {
    if (activeTab === "pending" && editColumn) {
      setEditColumn("");
      setColumnDrafts({});
    }
  }, [activeTab, editColumn]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // ------------- actions -------------
  const handleDelete = useCallback((id) => {
    swal("Warning", "Sure you want to delete it?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then((value) => {
      if (value === "delete") {
        _Delete(Delete_Receipt_By_Id, id)
          .then(() => {
            setAllRows((prev) => prev.filter((x) => x._id !== id));
          })
          .catch(() => {});
      }
    });
  }, []);

  useEffect(() => {
    handleDeleteRef.current = handleDelete;
  }, [handleDelete]);

  const handleExportXLSX = async (fileName) => {
    // Export every list column with readable/full column names,
    // regardless of which columns are currently visible on screen.
    // XLSX and file-saver are large dependencies. Load them only when the
    // user actually exports instead of making every page visit download them.
    const [{ default: XLSXDefault, ...XLSXNamed }, fileSaver] =
      await Promise.all([import("xlsx"), import("file-saver")]);
    const XLSX = XLSXDefault || XLSXNamed;
    const saveAs = fileSaver.saveAs || fileSaver.default;

    const cols = baseColumns;
    const headerRow = cols.map((c) => c.header);
    const dataRows = filteredRows.map((r) => cols.map((c) => toStr(c.get(r))));

    const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
    ws["!cols"] = cols.map((c) => ({
      wch: Math.max(14, Math.min(40, c.header.length + 8)),
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Receipt List");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`,
    );
  };

  // ------------- column-wise editing -------------
  const ensureParticularOptions = useCallback(async () => {
    const uid = activeContext?.uid;
    const db = activeContext?.db;
    if (!uid || !db) return;

    const contextKey = `${uid}|${db}`;
    if (particularLoadedFor === contextKey && particularOptions.length) return;

    setParticularLoading(true);
    try {
      const accountsURL = `${Create_Account_List}${uid}/`;
      const [customerRes, accountRes, expenseRes] = await Promise.allSettled([
        CreateCustomerList(uid, db),
        _Get(accountsURL, db),
        _Get(View_Expense_Account, db),
      ]);

      const customers = Array.isArray(customerRes?.value?.Customer)
        ? customerRes.value.Customer.map((c) => ({
            ...c,
            party: true,
            fullName:
              (c?.CompanyName && c.CompanyName.trim()) ||
              `${c?.firstName || ""} ${c?.lastName || ""}`.trim() ||
              c?.mobileNumber ||
              "Customer",
          }))
        : [];

      const users = Array.isArray(accountRes?.value?.adminDetails)
        ? accountRes.value.adminDetails.map((u) => ({
            ...u,
            User: true,
            fullName:
              `${u?.firstName || ""} ${u?.lastName || ""}`.trim() ||
              u?.email ||
              "User",
          }))
        : [];

      const expenses = Array.isArray(expenseRes?.value?.Expenses)
        ? expenseRes.value.Expenses.map((e) => ({
            ...e,
            miscellaneous: true,
            fullName:
              [e?.title, e?.type].filter(Boolean).join(" ").trim() || "Expense",
          }))
        : [];

      setParticularOptions(
        buildParticularOptions(
          [...customers, ...users, ...expenses],
          bankOptions,
        ),
      );
      setParticularLoadedFor(contextKey);
    } catch (error) {
      console.error("[ReceiptList] particular options error:", error);
      swal("Unable to load Particular dropdown options");
    } finally {
      setParticularLoading(false);
    }
  }, [
    activeContext,
    bankOptions,
    particularLoadedFor,
    particularOptions.length,
  ]);

  const getRowParticularValue = useCallback(
    (row) => {
      const partyId = getEntityId(row?.partyId);
      if (partyId) return `party:${partyId}`;

      const userId = getEntityId(row?.userId);
      if (userId) return `user:${userId}`;

      const expenseId = getEntityId(row?.expenseId);
      if (!expenseId) return "";
      if (String(expenseId) === CASH_ID) return `cash:${CASH_ID}`;
      if (bankOptions.some((b) => String(b?._id || "") === String(expenseId))) {
        return `bank:${expenseId}`;
      }
      return `expense:${expenseId}`;
    },
    [bankOptions],
  );

  const getEditValue = useCallback(
    (row, key) => {
      const rowId = toStr(row?._id);
      if (Object.prototype.hasOwnProperty.call(columnDrafts, rowId)) {
        return columnDrafts[rowId];
      }

      if (key === "date") return onlyDate(row?.date);
      if (key === "particular") return getRowParticularValue(row);
      if (key === "paymentMode") {
        const bankId = getReceiptBankId(row);
        return String(bankId).toUpperCase() === CASH_ID
          ? "Cash"
          : row?.paymentMode || "Bank";
      }
      if (key === "amount") return row?.amount ?? "";
      if (key === "remark") return row?.remark || "";
      return "";
    },
    [columnDrafts, getRowParticularValue],
  );

  const setDraftValue = useCallback((rowId, value) => {
    const id = toStr(rowId);
    if (!id) return;
    setColumnDrafts((prev) => ({ ...prev, [id]: value }));
  }, []);

  const handleEditColumnChange = async (e) => {
    const key = e.target.value;
    setEditColumn(key);
    setColumnDrafts({});
    setPage(1);

    if (key && !selectedKeys.includes(key)) {
      setSelectedKeys((prev) => [...prev, key]);
    }

    if (key === "particular") {
      await ensureParticularOptions();
    }
  };

  const cancelColumnEdit = () => {
    setEditColumn("");
    setColumnDrafts({});
    setSavingColumn(false);
  };

  const buildColumnUpdatePayload = (row, key, value) => {
    const selectedDatabase = getSelectedDatabase(activeContext?.db);
    const payload = {
      database: selectedDatabase,
      created_by: activeContext?.uid,
    };

    if (key === "date") {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) {
        throw new Error("Please enter a valid date");
      }
      payload.date = value;
      payload.financialYear = getFinancialYearLabel(value);
    } else if (key === "particular") {
      const option = particularOptions.find((o) => o.value === value);
      if (!option) throw new Error("Please select a valid Particular");

      payload.partyId = null;
      payload.userId = null;
      payload.expenseId = null;

      if (option.kind === "party") payload.partyId = option.id;
      else if (option.kind === "user") payload.userId = option.id;
      else if (option.kind === "expense") payload.expenseId = option.id;
      else if (option.kind === "bank") payload.expenseId = option.id;
      else if (option.kind === "cash") payload.expenseId = CASH_ID;

      const paymentMode = String(row?.paymentMode || "").toLowerCase();
      const isCashParty =
        option.kind === "party" &&
        String(option?.raw?.sId || "")
          .trim()
          .toUpperCase() === CASH_PARTY_SID;
      payload.isCashToBank = isCashParty && paymentMode === "bank";
      payload.isBankToCash = false;
    } else if (key === "paymentMode") {
      const mode = String(value || "Bank") === "Cash" ? "Cash" : "Bank";
      payload.paymentMode = mode;

      if (mode === "Cash") {
        payload.bankDetails = CASH_ID;
        payload.bankSelect = "Cash";
      } else {
        const currentBankId = getReceiptBankId(row);
        const currentIsRealBank =
          currentBankId && String(currentBankId).toUpperCase() !== CASH_ID;
        const selectedBank = currentIsRealBank
          ? bankOptions.find(
              (b) => String(b?._id || "") === String(currentBankId),
            )
          : bankOptions.find((b) => String(b?._id || "") !== CASH_ID);
        if (!selectedBank)
          throw new Error("No bank account is available for Bank mode");
        payload.bankDetails = selectedBank?._id || null;
        payload.bankSelect = selectedBank?.bankName || "";
      }

      const party = typeof row?.partyId === "object" ? row.partyId : null;
      payload.isCashToBank =
        String(party?.sId || "")
          .trim()
          .toUpperCase() === CASH_PARTY_SID && mode.toLowerCase() === "bank";
      payload.isBankToCash = false;
    } else if (key === "amount") {
      const amount = Number(value);
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error("Amount must be greater than 0");
      }
      payload.amount = amount;
    } else if (key === "remark") {
      payload.remark = String(value ?? "");
    }

    return payload;
  };

  const saveColumnChanges = async () => {
    const entries = Object.entries(columnDrafts);
    if (!editColumn) return;
    if (!entries.length) {
      swal("No changes made in this column");
      return;
    }

    const rowMap = new Map((allRows || []).map((r) => [toStr(r?._id), r]));

    try {
      setSavingColumn(true);
      const jobs = entries.map(([rowId, value]) => () => {
        const row = rowMap.get(rowId);
        if (!row) return Promise.resolve();
        const payload = buildColumnUpdatePayload(row, editColumn, value);
        return _Put(Update_Receipt_By_Id2, rowId, payload);
      });

      await runInBatches(jobs);
      const savedCount = entries.length;
      cancelColumnEdit();
      await reload(activeContext?.uid, activeContext?.db);
      swal(`${savedCount} receipt(s) updated successfully`);
    } catch (error) {
      console.error("[ReceiptList] column save error:", error);
      swal(
        "Error",
        error?.message || "Could not update the selected column",
        "error",
      );
      setSavingColumn(false);
    }
  };

  const renderColumnCell = (row, column) => {
    if (!editColumn || column.key !== editColumn) {
      return typeof column.render === "function"
        ? column.render(row)
        : toStr(column.get(row));
    }

    const rowId = toStr(row?._id);
    const value = getEditValue(row, column.key);

    if (column.key === "particular") {
      return (
        <Input
          type="select"
          value={value || ""}
          disabled={particularLoading}
          onChange={(e) => setDraftValue(rowId, e.target.value)}
        >
          <option value="">
            {particularLoading ? "Loading Particulars..." : "--Select--"}
          </option>
          {value &&
            !particularOptions.some((option) => option.value === value) && (
              <option value={value}>{getParticularText(row)}</option>
            )}
          {particularOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
      );
    }

    if (column.key === "paymentMode") {
      return (
        <Input
          type="select"
          value={value || "Bank"}
          onChange={(e) => setDraftValue(rowId, e.target.value)}
        >
          <option value="Bank">Bank</option>
          <option value="Cash">Cash</option>
        </Input>
      );
    }

    if (column.key === "date") {
      return (
        <Input
          type="date"
          value={value || ""}
          onChange={(e) => setDraftValue(rowId, e.target.value)}
        />
      );
    }

    if (column.key === "amount") {
      return (
        <Input
          type="number"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => setDraftValue(rowId, e.target.value)}
        />
      );
    }

    return (
      <Input
        type="text"
        value={value}
        onChange={(e) => setDraftValue(rowId, e.target.value)}
      />
    );
  };

  // ------------- columns modal logic -------------
  const openCols = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setColsModal(true);
  };
  const closeCols = () => setColsModal(false);

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current]),
    );
    setSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };

  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };

  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem("ReceiptListCols", JSON.stringify(selectedKeys));
    closeCols();
  };

  // ------------- loading -------------
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="rc-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Receipt List{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div className="rc-tabs" role="tablist" aria-label="Receipt status tabs">
                <button
                  type="button"
                  className={`rc-tab ${activeTab === "receipts" ? "active" : ""}`}
                  onClick={() => setActiveTab("receipts")}
                >
                  Receipts ({allRows.length})
                </button>
                <button
                  type="button"
                  className={`rc-tab ${activeTab === "pending" ? "active" : ""}`}
                  onClick={() => setActiveTab("pending")}
                >
                  Pending Verification ({pendingRows.length})
                </button>
              </div>

              {isMaster && (
                <div style={{ minWidth: 280 }}>
                  <SuperAdminUI
                    onDropdownChange={(val) =>
                      localStorage.setItem(
                        "SuperadminIdByMaster",
                        JSON.stringify(val),
                      )
                    }
                    onSubmit={(e) => {
                      e.preventDefault();
                      const v = JSON.parse(
                        localStorage.getItem("SuperadminIdByMaster") || "null",
                      );
                      if (!v) return;
                      const [id, db] = String(v).split(" ");
                      reload(id, db);
                    }}
                  />
                </div>
              )}

              <div style={{ flex: "1 1 140px" }} />

              <div style={{ minWidth: 320 }}>
                <Input
                  type="select"
                  value={bankFilter}
                  onChange={(e) => setBankFilter(e.target.value)}
                  style={{ background: "#fff" }}
                  title="Filter by bank account"
                  disabled={activeTab === "pending" || !bankOptions.length}
                >
                  {!bankOptions.length && (
                    <option value="">No Bank Accounts</option>
                  )}

                  {(bankOptions || []).map((b) => (
                    <option key={toStr(b?._id)} value={toStr(b?._id)}>
                      {bankLabel(b)}
                    </option>
                  ))}
                </Input>
              </div>

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search receipt..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {permissions?.Create && (
                <Button
                  style={btnPrimary}
                  onClick={() => navigate(`/app/receipts/create/0`)}
                >
                  <FaPlus size={10} /> Receipt
                </Button>
              )}

              {permissions?.Edit && activeTab === "receipts" && (
                <div style={{ minWidth: 170 }}>
                  <Input
                    type="select"
                    value={editColumn}
                    onChange={handleEditColumnChange}
                    style={{ height: 32, background: "#fff", fontWeight: 700 }}
                    title="Choose one column to edit"
                  >
                    <option value="">Edit Column</option>
                    {baseColumns.map((column) => (
                      <option key={column.key} value={column.key}>
                        {column.header}
                      </option>
                    ))}
                  </Input>
                </div>
              )}

              {activeTab === "receipts" && editColumn && (
                <>
                  <Button
                    color="success"
                    onClick={saveColumnChanges}
                    disabled={
                      savingColumn ||
                      particularLoading ||
                      !Object.keys(columnDrafts).length
                    }
                    style={{
                      height: 32,
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    {savingColumn
                      ? "Saving..."
                      : `Save ${baseColumns.find((c) => c.key === editColumn)?.header || "Column"} (${Object.keys(columnDrafts).length})`}
                  </Button>
                  <Button
                    color="secondary"
                    onClick={cancelColumnEdit}
                    disabled={savingColumn}
                    style={{
                      height: 32,
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    Cancel
                  </Button>
                </>
              )}

              <Button
                outline
                color="primary"
                onClick={openCols}
                title="Filter Columns"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              {permissions?.Download && (
                <ImDownload
                  onClick={() => handleExportXLSX("ReceiptList")}
                  style={{ cursor: "pointer" }}
                  title="Download Excel"
                  size={24}
                  color="rgb(8, 91, 245)"
                />
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {activeTab === "receipts" && editColumn && (
              <div
                style={{
                  marginBottom: 8,
                  padding: "8px 10px",
                  borderRadius: 10,
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  color: "#9a3412",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                Editing only the{" "}
                <b>{baseColumns.find((c) => c.key === editColumn)?.header}</b>{" "}
                column. Change any rows you need, then click Save. Other columns
                stay read-only.
              </div>
            )}
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                {activeTab === "pending"
                  ? "No receipts are waiting for cash collector verification."
                  : "No receipts found."}
              </div>
            ) : (
              <div className="rc-table-wrap">
                <table className="rc-table">
                  <thead>
                    <tr>
                      {tableCols.map((column) => (
                        <th
                          key={column.key}
                          className={
                            editColumn === column.key ? "rc-editing-head" : ""
                          }
                        >
                          {column.header}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {pageRows.map((row) => (
                      <tr key={row?._id}>
                        {tableCols.map((column) => (
                          <td
                            key={column.key}
                            className={
                              editColumn === column.key ? "rc-editing-cell" : ""
                            }
                          >
                            {renderColumnCell(row, column)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>

                  <tfoot>
                    <tr>
                      {tableCols.map((column, index) => {
                        if (column.key === "amount") {
                          return (
                            <td key={column.key}>
                              {Number(totalAmount || 0).toFixed(2)}
                            </td>
                          );
                        }
                        if (index === 0) return <td key={column.key}>TOTAL</td>;
                        return <td key={column.key}></td>;
                      })}
                    </tr>
                  </tfoot>
                </table>
                {filteredRows.length > effectivePageSize && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 4px 2px",
                    }}
                  >
                    <Button
                      size="sm"
                      outline
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      size="sm"
                      outline
                      disabled={page >= totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        isOpen={colsModal}
        toggle={closeCols}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeCols}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div style={listBoxStyle}>
                {baseColumns.map((col) => (
                  <div key={col.key} style={pickItemStyle}>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div style={{ ...listBoxStyle, flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          ...pickItemStyle,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}

/* little styles */
const btnPrimary = {
  backgroundColor: "rgb(8, 91, 245)",
  color: "white",
  fontWeight: 600,
  height: 32,
  fontSize: 14,
  textTransform: "uppercase",
};
const listBoxStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  maxHeight: 280,
  overflow: "auto",
  background: "#fff",
  padding: 8,
};
const pickItemStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  marginBottom: 6,
  background: "#fff",
};
